<template>
  <UniversityLectureLayout v-if="course && lecture" :course="course" :lecture="lecture">
    <div class="mx-auto w-full max-w-4xl">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p v-if="currentSection" class="text-xs font-semibold uppercase tracking-[0.16em] text-[#4D2542]">
            {{ currentSection.name }}
          </p>
          <h1 class="mt-1 text-2xl font-bold leading-snug tracking-tight text-gray-900">{{ lecture.name }}</h1>
        </div>

        <button
          type="button"
          :disabled="pending"
          class="mt-1 inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
          :class="
            lecture.completed
              ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
              : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
          "
          @click="toggle(lecture)"
        >
          <vue-feather :type="lecture.completed ? 'check-circle' : 'circle'" size="16" />
          {{ lecture.completed ? 'Completed' : 'Mark complete' }}
        </button>
      </div>

      <div class="mt-5">
        <LecturePlayer :key="lecture.id" :material-url="lecture.material_url" @ended="onVideoEnded" />
      </div>

      <nav class="mt-4 flex items-center justify-between gap-3">
        <RouterLink
          v-if="prevLecture"
          :to="{ name: 'University Lecture', params: { course_slug: course.slug, order: prevLecture.order } }"
          class="group flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
        >
          <vue-feather type="chevron-left" size="16" class="shrink-0 text-gray-400" />
          <span class="min-w-0 truncate">{{ prevLecture.name }}</span>
        </RouterLink>
        <span v-else></span>

        <RouterLink
          v-if="nextLecture"
          :to="{ name: 'University Lecture', params: { course_slug: course.slug, order: nextLecture.order } }"
          class="group flex min-w-0 items-center gap-2 rounded-lg bg-[#202F7E] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#19246F]"
        >
          <span class="min-w-0 truncate">{{ nextLecture.name }}</span>
          <vue-feather type="chevron-right" size="16" class="shrink-0" />
        </RouterLink>
      </nav>

      <LectureDetail :lecture="lecture" />
    </div>

    <LectureCompleteDialog
      v-model:open="showCompleteDialog"
      :lecture-name="lecture.name"
      :has-next="!!nextLecture"
      @confirm="confirmComplete"
    />
  </UniversityLectureLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Api } from '@/api';
import UniversityLectureLayout from '@/components/layouts/UniversityLectureLayout/index.vue';
import LectureDetail from '@/components/agent/university/LectureDetail.vue';
import LecturePlayer from '@/components/agent/university/LecturePlayer.vue';
import LectureCompleteDialog from '@/components/agent/university/LectureCompleteDialog.vue';
import { useLectureCompletion } from '@/components/agent/university/useLectureCompletion';
import type { CourseWithSections } from '@/api/agent/university/course';

const route = useRoute();
const router = useRouter();

const course = ref<CourseWithSections | null>(null);

const showCompleteDialog = ref(false);

const { toggle, markComplete, pending } = useLectureCompletion();

const lectureOrder = computed(() => Number(route.params.order));

const lectures = computed(() =>
  course.value ? course.value.course_sections.flatMap(section => section.lectures) : []
);

const lecture = computed(() => lectures.value.find(l => l.order === lectureOrder.value));

const currentIndex = computed(() => lectures.value.findIndex(l => l.order === lectureOrder.value));

const prevLecture = computed(() => (currentIndex.value > 0 ? lectures.value[currentIndex.value - 1] : null));

const nextLecture = computed(() =>
  currentIndex.value >= 0 && currentIndex.value < lectures.value.length - 1
    ? lectures.value[currentIndex.value + 1]
    : null
);

const currentSection = computed(
  () =>
    course.value?.course_sections.find(section => section.lectures.some(l => l.order === lectureOrder.value)) ?? null
);

const onVideoEnded = () => {
  if (lecture.value && !lecture.value.completed) showCompleteDialog.value = true;
};

const confirmComplete = async () => {
  if (!lecture.value || !course.value) return;

  await markComplete(lecture.value);
  showCompleteDialog.value = false;

  if (nextLecture.value) {
    router.push({
      name: 'University Lecture',
      params: { course_slug: course.value.slug, order: nextLecture.value.order },
    });
  }
};

const loadCourse = async () => {
  const response = await Api.AgentUniversityCourse.getCourse(String(route.params.course_slug));
  if (response.status === 'success') {
    course.value = response.data;
  }
};

onMounted(loadCourse);
</script>
